import React from 'react';
import { Link } from 'react-router-dom';
import { Checkbox } from '@this/shared/ui/inputs/checkbox';
import styled from 'styled-components';
import { lighten } from '@material-ui/core/styles';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import moment from 'moment';
import type { ReceiptRecord } from '@this/src/types/receipt_record';
import { media } from '../../shared/atoms/media';

interface TripReceiptRowProps {
  receiptRecord: ReceiptRecord;
  selectedTrips: string[];
  inProgressTripIds: string[];
  onSelectTrip: (tripId: string) => void;
  isDisabled: boolean;
}

export const TripReceiptRow: React.FC<TripReceiptRowProps> = React.memo(
  ({ receiptRecord, selectedTrips, inProgressTripIds, onSelectTrip, isDisabled }) => {
    const formatDate = (dateString: string | null) => {
      if (!dateString) return '';
      return moment(dateString, moment.ISO_8601).format('YYYY/MM/DD');
    };

    const formatDateTime = (dateString: string | null) => {
      if (!dateString) return '';
      return moment(dateString, moment.ISO_8601).format('YYYY/MM/DD HH:mm');
    };

    const formatPrice = (price: number) => {
      return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    const getDateRange = () => {
      if (receiptRecord.cache_start_time && receiptRecord.cache_end_time) {
        const startDate = formatDate(receiptRecord.cache_start_time.toString());
        const endDate = formatDate(receiptRecord.cache_end_time.toString());
        return `${startDate}～${endDate}`;
      }
      return '';
    };

    const dateRange = getDateRange();
    const originText = receiptRecord.departure || '';
    const destinationText = receiptRecord.destination || '';
    const totalPrice = receiptRecord.total_price || 0;

    const createdAtStr = receiptRecord.created_at ? receiptRecord.created_at.toString() : null;

    const hasDownloadInfo =
      (typeof receiptRecord.downloaded_count === 'number' && receiptRecord.downloaded_count > 0) ||
      (receiptRecord.downloaded_at && receiptRecord.downloaded_at !== null);

    return (
      <StyledSimpleCardWrap>
        <StyledSimpleCard key={`${receiptRecord.id}-${receiptRecord.invoice_id}`}>
          <StyledCardFlex>
            <StyledCardFlexLeftCheck>
              <Checkbox
                checked={selectedTrips.includes(`${receiptRecord.id}-${receiptRecord.invoice_id}`)}
                onChange={() => onSelectTrip(`${receiptRecord.id}-${receiptRecord.invoice_id}`)}
                disabled={
                  isDisabled || inProgressTripIds.includes(`${receiptRecord.id}-${receiptRecord.invoice_id}`)
                }
              />
            </StyledCardFlexLeftCheck>
            <StyledCardFlexLeft>
              <StyledContentFlex>
                <StyledContentFlexLeft>
                  <StyledContentRow>
                    <StyledContentTitle>
                      旅程番号：<span data-wovn-ignore>{receiptRecord.id}</span>
                      {dateRange && (
                        <>
                          （<span data-wovn-ignore>{dateRange}</span>）
                        </>
                      )}
                    </StyledContentTitle>
                  </StyledContentRow>
                  <StyledContentRow>
                    <StyledContentText>
                      出発地：
                      {originText ? (
                        <span data-wovn-ignore>{originText}</span>
                      ) : (
                        <StyledEmptyText>なし</StyledEmptyText>
                      )}
                    </StyledContentText>
                  </StyledContentRow>
                  <StyledContentRow>
                    <StyledContentText>
                      目的地：
                      {destinationText ? (
                        <span data-wovn-ignore>{destinationText}</span>
                      ) : (
                        <StyledEmptyText>なし</StyledEmptyText>
                      )}
                    </StyledContentText>
                  </StyledContentRow>
                  <StyledContentRow>
                    <StyledContentText>
                      旅程作成日時：<span data-wovn-ignore>{formatDateTime(createdAtStr)}</span>
                    </StyledContentText>
                  </StyledContentRow>
                  <StyledSpOnlyContent>
                    <StyledContentRow>
                      {totalPrice ? (
                        <StyledTotalPrice>
                          <span>合計：</span>
                          <span data-wovn-ignore>{formatPrice(totalPrice)}</span>
                          <span>円</span>
                        </StyledTotalPrice>
                      ) : (
                        <StyledTotalPrice>合計：確認中</StyledTotalPrice>
                      )}
                    </StyledContentRow>
                    {hasDownloadInfo && (
                      <>
                        <StyledContentRow>
                          <StyledDownloadText>
                            ダウンロード回数：<span data-wovn-ignore>{receiptRecord.downloaded_count || 0}</span>
                          </StyledDownloadText>
                        </StyledContentRow>
                        {receiptRecord.downloaded_at && (
                          <StyledContentRow>
                            <StyledDownloadText>
                              前回のダウンロード：
                              <span data-wovn-ignore>{formatDate(receiptRecord.downloaded_at.toString())}</span>
                            </StyledDownloadText>
                          </StyledContentRow>
                        )}
                      </>
                    )}
                  </StyledSpOnlyContent>
                </StyledContentFlexLeft>
                <StyledContentFlexRight>
                  <StyledTotalPriceContainer>
                    {totalPrice ? (
                      <StyledTotalPrice>
                        <span>合計：</span>
                        <span data-wovn-ignore>{formatPrice(totalPrice)}</span>
                        <span>円</span>
                      </StyledTotalPrice>
                    ) : (
                      <StyledTotalPrice>合計：確認中</StyledTotalPrice>
                    )}
                  </StyledTotalPriceContainer>

                  {hasDownloadInfo && (
                    <StyledDownloadInfoContainer>
                      <StyledDownloadText>
                        ダウンロード回数：<span data-wovn-ignore>{receiptRecord.downloaded_count || 0}</span>
                      </StyledDownloadText>
                      {receiptRecord.downloaded_at && (
                        <StyledDownloadText>
                          前回のダウンロード：
                          <span data-wovn-ignore>{formatDate(receiptRecord.downloaded_at.toString())}</span>
                        </StyledDownloadText>
                      )}
                    </StyledDownloadInfoContainer>
                  )}
                </StyledContentFlexRight>
              </StyledContentFlex>
            </StyledCardFlexLeft>
            <StyledCardFlexRight
              to={
                receiptRecord.invoice_id
                  ? `/trip_receipts/${receiptRecord.id}/invoices/${receiptRecord.invoice_id}`
                  : `/trip_receipts/${receiptRecord.id}/credit_card`
              }
            >
              <ArrowForwardIcon />
            </StyledCardFlexRight>
          </StyledCardFlex>
        </StyledSimpleCard>
      </StyledSimpleCardWrap>
    );
  }
);

const StyledSimpleCardWrap = styled.div`
  margin-bottom: 25px;
  border-radius: 5px;
`;

const StyledSimpleCard = styled.div`
  background: #fff;
  border-radius: 5px;
`;

const StyledCardFlex = styled.div`
  display: flex;
  overflow: hidden;
  border-radius: 5px;
`;

const StyledCardFlexLeftCheck = styled.div`
  flex: 0 0 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const StyledCardFlexLeft = styled.div`
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
`;

const StyledCardFlexRight = styled(Link)`
  flex: 0 0 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background-color: ${props => props.theme.grayBgColorLight};
  transition: background-color 0.3s;

  & svg {
    color: ${props => props.theme.grayTextColor};
    border: solid 2px ${props => lighten(props.theme.grayTextColor, 0.3)};
    border-radius: 50%;
    padding: 2px;
    transition: color 0.3s, border-color 0.3s;
  }

  &:hover {
    background-color: ${props => lighten(props.theme.grayBgColorLight, 0.1)};

    & svg {
      color: ${props => lighten(props.theme.grayTextColor, 0.3)};
      border-color: ${props => lighten(props.theme.grayTextColor, 0.6)};
    }
  }
`;

const StyledContentFlex = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  height: 100%;

  ${media.sp`
    flex-direction: column;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 16px;
    min-height: auto;
    width: 100%;
  `}
`;

const StyledContentFlexLeft = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 5px;
  flex: 1;

  ${media.sp`
    width: 100%;
  `}
`;

const StyledContentFlexRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 200px;
  height: 100%;
  justify-content: space-between;
  padding-bottom: 0;

  ${media.sp`
    display: none;
  `}
`;

const StyledContentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StyledContentTitle = styled.h3`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 0;
`;

const StyledContentText = styled.p`
  font-size: 14px;
  margin: 0;
  color: ${props => props.theme.grayTextColor};
`;

const StyledEmptyText = styled.span`
  color: ${props => props.theme.grayTextColor};
`;

const StyledTotalPriceContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 200px;

  ${media.sp`
    width: 100%;
    min-width: unset;
  `}
`;

const StyledTotalPrice = styled.p`
  font-weight: bold;
  font-size: 16px;
  margin: 0;
  width: 100%;
`;

const StyledDownloadInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;

const StyledDownloadText = styled.p`
  font-size: 12px;
  margin: 2px 0;
  color: ${props => props.theme.grayTextColor};
  text-align: left;
  width: 100%;
`;

const StyledSpOnlyContent = styled.div`
  display: none;

  ${media.sp`display: block;`}
`;

export default TripReceiptRow;
